import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import { ApiFactory } from '../api/easypos-api';
import { resolveRouterPath, router } from '../router';
import { write } from '../language/lang';
import { styles as sharedStyles } from '../styles/shared-styles';

// Shop names go on URLs and become the per-shop blob container suffix on the
// server, so we restrict them to a slug-friendly subset client-side too.
// Server is the authority — this is just first-pass validation for the form.
const SHOP_SLUG = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?$/;

@customElement('shop-create-page')
export class ShopCreatePage extends LitElement {
  @state() private _shopName = '';
  @state() private _displayName = '';
  @state() private _timezone = browserTimezone();
  @state() private _busy = false;
  @state() private _error: string | null = null;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 480px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .hint {
        font-size: 0.85rem;
        opacity: 0.75;
      }
    `,
  ];

  private _onInput(field: 'shopName' | 'displayName' | 'timezone') {
    return (e: Event) => {
      const value = (e.target as HTMLInputElement).value;
      if (field === 'shopName') this._shopName = value.toLowerCase().trim();
      if (field === 'displayName') this._displayName = value;
      if (field === 'timezone') this._timezone = value;
    };
  }

  private async _submit(e: Event) {
    e.preventDefault();
    if (this._busy) return;
    this._error = null;

    const shopName = this._shopName.trim();
    const displayName = this._displayName.trim();
    if (!shopName || !displayName) {
      this._error = 'Shop name and display name are required.';
      return;
    }
    if (!SHOP_SLUG.test(shopName)) {
      this._error =
        'Shop name must be 3–32 chars, lowercase letters, numbers, hyphens. Cannot start or end with a hyphen.';
      return;
    }

    this._busy = true;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.createShop({
        shopName,
        displayName,
        timezone: this._timezone || 'UTC',
        // Visibility intentionally omitted — server defaults to private. Owner
        // flips to public from the settings page once the shop is set up.
      });
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Could not create shop.';
        return;
      }
      // The new token (with this shop in ShopAccess) is now on the API client.
      // Tell the app shell to refresh user-profile + active-shop, then go home.
      window.dispatchEvent(
        new CustomEvent('shop-created', {
          detail: {
            token: r.data.token,
            shop: r.data.shop,
          },
        })
      );
      router.navigate(resolveRouterPath(''));
    } catch (err) {
      this._error = (err as Error)?.message ?? 'Unexpected error.';
    } finally {
      this._busy = false;
    }
  }

  render() {
    return html`
      <sl-card>
        <h2 slot="header">
          ${write`${'ref:shop:create-title'}Create your shop`}
        </h2>

        <form @submit=${this._submit}>
          <sl-input
            label=${write`${'ref:shop:slug'}Shop URL slug`}
            help-text="Lowercase, hyphens allowed. Used in URLs (e.g. /m/your-cafe)."
            .value=${this._shopName}
            @sl-input=${this._onInput('shopName')}
            autocomplete="off"
            spellcheck="false"
            required
          ></sl-input>

          <sl-input
            label=${write`${'ref:shop:display-name'}Display name`}
            help-text="The name customers see."
            .value=${this._displayName}
            @sl-input=${this._onInput('displayName')}
            required
          ></sl-input>

          <sl-input
            label=${write`${'ref:shop:timezone'}Timezone`}
            help-text="IANA timezone, e.g. Australia/Sydney. Used to determine when each day closes."
            .value=${this._timezone}
            @sl-input=${this._onInput('timezone')}
          ></sl-input>

          <p class="hint">
            ${write`${'ref:shop:create-private-hint'}New shops start in Private mode — only you and your staff can use it. Flip it to Public from settings when you're ready for customers.`}
          </p>

          ${
            this._error
              ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>`
              : nothing
          }

          <sl-button
            type="submit"
            variant="primary"
            ?loading=${this._busy}
            ?disabled=${this._busy}
          >
            ${write`${'ref:shop:create-submit'}Create shop`}
          </sl-button>
        </form>
      </sl-card>
    `;
  }
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
